import { useState } from 'react';
import { ChevronDown, ChevronUp, Package, Users01, MarkerPin01, CurrencyDollar, Settings01 } from '@untitledui/icons';
import { Badge } from '@/components/base/badges/badges';
import { Button } from '@/components/base/buttons/button';
import { ProgressBar } from '@/components/base/progress-indicators/progress-indicators';
import { cx } from '@/utils/cx';
import type { WeekData } from '@/utils/week-utils';
import { formatWeekRange, calculateTargetPercentage, getConfirmedVsPending } from '@/utils/week-utils';
import { useTranslation } from 'react-i18next';
import { receiverTargetService } from '@/data/mock-receiver-targets';

interface WeekCardProps {
    weekData: WeekData;
    receiverId: string;
    year: number;
    onEditTarget?: (weekNumber: number) => void;
    className?: string;
}

export const WeekCard = ({ weekData, receiverId, year, onEditTarget, className }: WeekCardProps) => {
    const [isExpanded, setIsExpanded] = useState(false);
    const { t } = useTranslation(['pages:calendar', 'common']);

    const {
        weekNumber,
        totalContainers,
        uniqueOrigins,
        uniqueSenders,
        totalValue,
        isCurrentWeek,
        containers
    } = weekData;

    const weekRange = formatWeekRange(weekData);

    // Calcola target e percentuali
    const target = receiverTargetService.getTarget(receiverId, year, weekNumber);
    const containerTarget = target?.containerTarget || 0;
    const revenueTarget = target?.revenueTarget || 0;
    const containerPercentage = calculateTargetPercentage(totalContainers, containerTarget);
    const revenuePercentage = calculateTargetPercentage(totalValue.amount, revenueTarget);
    const hasReachedContainerTarget = containerPercentage >= 100;
    const hasReachedRevenueTarget = revenuePercentage >= 100;

    const { confirmed, pending } = getConfirmedVsPending(containers);

    const toggleExpanded = () => {
        setIsExpanded(!isExpanded);
    };

    // Raggruppa container per commodity per la visualizzazione
    const commodityGroups = containers.reduce((acc, container) => {
        const commodity = container.commodity;
        if (!acc[commodity]) {
            acc[commodity] = [];
        }
        acc[commodity].push(container);
        return acc;
    }, {} as Record<string, typeof containers>);

    return (
        <div className={cx(
            "bg-primary rounded-lg border border-secondary_alt p-4 transition-all duration-200",
            isCurrentWeek && "ring-2 ring-utility-brand-600 border-utility-brand-200",
            className
        )}>
            {/* Header */}
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <div className="flex items-center gap-2">
                        <h3 className="text-lg font-semibold text-primary">
                            {t('weekNumber', { number: weekNumber })}
                        </h3>
                        {isCurrentWeek && (
                            <Badge color="brand" size="sm">
                                {t('currentWeek')}
                            </Badge>
                        )}
                        {hasReachedContainerTarget && hasReachedRevenueTarget && (
                            <Badge color="success" size="sm">
                                {t('targetReached')}
                            </Badge>
                        )}
                        {(!hasReachedContainerTarget || !hasReachedRevenueTarget) && containerTarget > 0 && (
                            <Badge color="warning" size="sm">
                                {t('targetPending')}
                            </Badge>
                        )}
                    </div>
                    <span className="text-sm text-tertiary">
                        {weekRange}
                    </span>
                </div>

                <div className="flex items-center gap-2">
                    <Button
                        color="secondary"
                        size="sm"
                        iconLeading={Settings01}
                        onClick={() => onEditTarget?.(weekNumber)}
                    >
                        {t('editTarget')}
                    </Button>
                    {totalContainers > 0 && (
                        <Button
                            color="secondary"
                            size="sm"
                            iconTrailing={isExpanded ? ChevronUp : ChevronDown}
                            onClick={toggleExpanded}
                        >
                            {isExpanded ? t('collapseDetails') : t('expandDetails')}
                        </Button>
                    )}
                </div>
            </div>

            {/* Target Progress */}
            {containerTarget > 0 && (
                <div className="mt-4 space-y-3">
                    <div>
                        <div className="flex items-center justify-between mb-1">
                            <span className="text-xs text-tertiary">{t('containerTarget')}</span>
                            <span className="text-xs font-medium text-primary">
                                {totalContainers} / {containerTarget} ({containerPercentage}%)
                            </span>
                        </div>
                        <ProgressBar 
                            value={containerPercentage} 
                            progressClassName={hasReachedContainerTarget ? 'bg-success-primary' : 'bg-warning-primary'}
                        />
                    </div>
                    <div>
                        <div className="flex items-center justify-between mb-1">
                            <span className="text-xs text-tertiary">{t('revenueTarget')}</span>
                            <span className="text-xs font-medium text-primary">
                                ${totalValue.amount.toLocaleString()} / ${revenueTarget.toLocaleString()} ({revenuePercentage}%)
                            </span>
                        </div>
                        <ProgressBar 
                            value={revenuePercentage} 
                            progressClassName={hasReachedRevenueTarget ? 'bg-success-primary' : 'bg-warning-primary'}
                        />
                    </div>
                </div>
            )}

            {/* Summary Stats */}
            <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="flex items-center gap-2">
                    <Package className="h-4 w-4 text-secondary" />
                    <div>
                        <div className="text-sm font-medium text-primary">
                            {totalContainers}
                        </div>
                        <div className="text-xs text-tertiary">
                            {t('containers')} ({confirmed} {t('confirmed')}, {pending} {t('pending')})
                        </div>
                    </div>
                </div>

                <div className="flex items-center gap-2">
                    <MarkerPin01 className="h-4 w-4 text-secondary" />
                    <div>
                        <div className="text-sm font-medium text-primary">
                            {uniqueOrigins.length}
                        </div>
                        <div className="text-xs text-tertiary">
                            {t('origins')}
                        </div>
                    </div>
                </div>

                <div className="flex items-center gap-2">
                    <Users01 className="h-4 w-4 text-secondary" />
                    <div>
                        <div className="text-sm font-medium text-primary">
                            {uniqueSenders.length}
                        </div>
                        <div className="text-xs text-tertiary">
                            {t('senders')}
                        </div>
                    </div>
                </div>

                <div className="flex items-center gap-2">
                    <CurrencyDollar className="h-4 w-4 text-secondary" />
                    <div>
                        <div className="text-sm font-medium text-primary">
                            ${totalValue.amount.toLocaleString()}
                        </div>
                        <div className="text-xs text-tertiary">
                            {t('totalValue')}
                        </div>
                    </div>
                </div>
            </div>

            {/* No containers message */}
            {totalContainers === 0 && (
                <div className="mt-4 text-center py-8 text-tertiary">
                    <Package className="h-8 w-8 mx-auto mb-2 opacity-50" />
                    <p className="text-sm">{t('noContainers')}</p>
                </div>
            )}

            {/* Expanded Details */}
            {isExpanded && totalContainers > 0 && (
                <div className="mt-6 space-y-4 border-t border-secondary_alt pt-4">
                    {/* Commodities */}
                    <div>
                        <h4 className="text-sm font-medium text-primary mb-2">
                            {t('commodities')}
                        </h4>
                        <div className="flex flex-wrap gap-2">
                            {Object.entries(commodityGroups).map(([commodity, containers]) => (
                                <Badge
                                    key={commodity}
                                    color="gray"
                                    size="sm"
                                >
                                    {commodity} ({containers.length})
                                </Badge>
                            ))}
                        </div>
                    </div>

                    {/* Origins */}
                    <div>
                        <h4 className="text-sm font-medium text-primary mb-2">
                            {t('origins')}
                        </h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                            {uniqueOrigins.map((origin, index) => (
                                <div
                                    key={index}
                                    className="text-sm text-secondary bg-secondary rounded-md px-2 py-1"
                                >
                                    {origin}
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Container Details */}
                    <div>
                        <h4 className="text-sm font-medium text-primary mb-2">
                            {t('containers')} ({totalContainers})
                        </h4>
                        <div className="space-y-2">
                            {containers.map((container) => (
                                <div
                                    key={container.id}
                                    className="flex items-center justify-between p-3 bg-secondary rounded-md"
                                >
                                    <div className="flex items-center gap-3">
                                        <div>
                                            <div className="text-sm font-medium text-primary">
                                                {container.containerNumber}
                                            </div>
                                            <div className="text-xs text-tertiary">
                                                {container.route.from} → {container.route.to}
                                            </div>
                                            <div className="text-xs text-tertiary mt-1">
                                                ETA: {container.eta.toLocaleDateString('it-IT')}
                                            </div>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <Badge
                                            color={
                                                container.status === 'Delivered' ? 'success' :
                                                    container.status === 'Delayed' ? 'error' :
                                                        container.status === 'At Port' ? 'warning' :
                                                            'gray'
                                            }
                                            size="sm"
                                        >
                                            {container.status}
                                        </Badge>
                                        <span className="text-xs text-tertiary">
                                            ${container.value.amount.toLocaleString()}
                                        </span>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};
